export interface DiagramTemplate {
  id: string;
  title: string;
  description: string;
  notation: string;
  source: string;
}

export const DIAGRAM_TEMPLATES: readonly DiagramTemplate[] = [
  { id: "flowchart", title: "流程图", description: "业务流程、判断分支与处理步骤", notation: "流程图", source: `flowchart TD
  A([开始]) --> B[提交申请]
  B --> C{审核通过?}
  C -->|是| D[执行业务]
  C -->|否| E[补充材料]
  E --> B
  D --> F([结束])` },
  { id: "sequence", title: "时序图", description: "参与者、接口调用与响应顺序", notation: "UML 时序图", source: `sequenceDiagram
  actor U as 用户
  participant C as 客户端
  participant S as 服务端
  participant DB as 数据库
  U->>C: 提交登录
  C->>S: 验证凭据
  S->>DB: 查询用户
  DB-->>S: 用户信息
  alt 验证成功
    S-->>C: 返回访问令牌
    C-->>U: 进入首页
  else 验证失败
    S-->>C: 返回错误
    C-->>U: 提示重试
  end` },
  { id: "class", title: "类图", description: "类、属性、方法与继承关联", notation: "UML 类图", source: `classDiagram
  class User {
    +String id
    +String name
    +createOrder() Order
  }
  class Order {
    +String id
    +String status
    +submit()
    +cancel()
  }
  class OrderItem {
    +String productId
    +int quantity
    +Decimal price
  }
  User "1" --> "0..*" Order : creates
  Order "1" *-- "1..*" OrderItem : contains` },
  { id: "state", title: "状态图", description: "对象生命周期和状态转换条件", notation: "UML 状态图", source: `stateDiagram-v2
  state "待支付" as Pending
  state "已支付" as Paid
  state "配送中" as Shipping
  state "已完成" as Done
  state "已取消" as Cancelled
  [*] --> Pending
  Pending --> Paid: 支付成功
  Pending --> Cancelled: 超时或取消
  Paid --> Shipping: 发货
  Shipping --> Done: 确认收货
  Done --> [*]
  Cancelled --> [*]` },
  { id: "er", title: "ER 图", description: "数据库实体、字段与关系基数", notation: "实体关系图", source: `erDiagram
  USER ||--o{ ORDER : places
  ORDER ||--|{ ORDER_ITEM : contains
  PRODUCT ||--o{ ORDER_ITEM : referenced_by
  USER {
    string id PK
    string name
    string email UK
  }
  ORDER {
    string id PK
    string user_id FK
    datetime created_at
  }
  ORDER_ITEM {
    string id PK
    string order_id FK
    string product_id FK
    int quantity
  }
  PRODUCT {
    string id PK
    string name
    decimal price
  }` },
  { id: "usecase", title: "用例图", description: "参与者、系统边界、用例与包含关系", notation: "用例关系示意 · 流程图语法", source: `flowchart LR
  U["参与者：用户"]
  Admin["参与者：管理员"]
  subgraph System[订单系统]
    direction TB
    Login([登录])
    Browse([浏览商品])
    Place([提交订单])
    Pay([支付订单])
    Manage([管理商品])
  end
  U --- Login
  U --- Browse
  U --- Place
  Place -.->|«include»| Pay
  Admin --- Login
  Admin --- Manage` },
  { id: "architecture", title: "架构图", description: "系统分层、服务边界与数据依赖", notation: "系统架构示意", source: `flowchart TB
  subgraph Client[接入层]
    Web[Web 客户端]
    App[移动应用]
  end
  Gateway[API 网关]
  subgraph Services[业务服务层]
    Auth[身份服务]
    Order[订单服务]
    Catalog[商品服务]
  end
  subgraph Data[数据层]
    DB[(业务数据库)]
    Cache[(缓存)]
    MQ[消息队列]
  end
  Web --> Gateway
  App --> Gateway
  Gateway --> Auth
  Gateway --> Order
  Gateway --> Catalog
  Order --> DB
  Catalog --> Cache
  Order --> MQ` },
  { id: "component", title: "组件图", description: "组件职责、接口与组件间依赖", notation: "组件依赖示意 · 流程图语法", source: `flowchart LR
  subgraph Frontend[前端应用]
    UI["«component» 页面组件"]
    SDK["«component» API 客户端"]
  end
  subgraph Backend[后端应用]
    API["«interface» 订单 API"]
    Service["«component» 订单服务"]
    Repo["«component» 数据访问"]
  end
  UI --> SDK
  SDK -.->|调用| API
  API --> Service
  Service --> Repo
  Repo --> DB[(数据库)]` },
  { id: "deployment", title: "部署图", description: "运行节点、部署实例与网络通信", notation: "部署拓扑示意 · 流程图语法", source: `flowchart TB
  Browser[浏览器]
  subgraph Edge[边缘网络]
    LB[负载均衡]
  end
  subgraph Cluster[应用集群]
    subgraph Node1[应用节点 1]
      A1[应用实例 A]
    end
    subgraph Node2[应用节点 2]
      A2[应用实例 B]
    end
  end
  subgraph Storage[数据网络]
    Primary[(主数据库)]
    Replica[(只读副本)]
    Redis[(缓存)]
  end
  Browser -->|HTTPS| LB
  LB --> A1
  LB --> A2
  A1 --> Primary
  A2 --> Primary
  A1 --> Redis
  A2 --> Redis
  Primary -->|复制| Replica` },
];

export function diagramTemplate(id?: string): DiagramTemplate {
  return DIAGRAM_TEMPLATES.find((template) => template.id === id) ?? DIAGRAM_TEMPLATES[0]!;
}

export function detectDiagramTemplate(source: string): DiagramTemplate | undefined {
  const exact = DIAGRAM_TEMPLATES.find((template) => template.source === source);
  if (exact) return exact;
  const prefix = source.trimStart();
  const id = prefix.startsWith("sequenceDiagram") ? "sequence" : prefix.startsWith("classDiagram") ? "class" : prefix.startsWith("stateDiagram") ? "state" : prefix.startsWith("erDiagram") ? "er" : undefined;
  return DIAGRAM_TEMPLATES.find((template) => template.id === id);
}
